import { nextTick, onUnmounted, watch, type WatchSource } from 'vue'
import { useRouter } from 'vue-router'
import { useQuickActionsStore, type QuickActionItem, type QuickActionsConfig } from '@/stores/quickActions/quickActionsStore'

export type { QuickActionItem, QuickActionsConfig }

type CallbackFields = {
  onPrimary?: (() => void) | null
  onAction?: ((action: string) => void) | null
  onHighlights?: (() => void) | null
  onFilter?: (() => void) | null
  onSearch?: ((query: string) => void) | null
  onSearchClose?: (() => void) | null
  onCreate?: (() => void) | null
  onShare?: ((e?: MouseEvent) => void) | null
}

type ConfigWithCallbacks = Partial<QuickActionsConfig> & CallbackFields

/**
 * Composable for pages to configure the global QuickActionsMenu.
 *
 * Usage:
 * ```ts
 * const qa = useQuickActions({
 *   primaryIcon: 'mdi-plus',
 *   showFilter: true,
 *   onPrimary: () => { showModal.value = true },
 *   onFilter: () => { showFilterSheet.value = true },
 * })
 *
 * // Reactively update later:
 * qa.patch({ filterActive: true })
 *
 * // For persistent (never-unmounting) components, reclaim the store
 * // when a reactive condition becomes true:
 * qa.reclaimWhen(() => isActiveTab.value)
 * ```
 *
 * Automatically resets on component unmount.
 */
let _nextInstanceId = 1

export function useQuickActions(initialConfig?: ConfigWithCallbacks) {
  const store = useQuickActionsStore()
  const instanceId = _nextInstanceId++

  // Static config (non-synced fields from initial + patch calls)
  const staticConfig: ConfigWithCallbacks = { ...(initialConfig ?? {}) }

  // Reactive getters registered via sync() — evaluated live on reclaim
  const syncGetters = new Map<string, () => any>()

  /** Build a fresh config by merging static fields with live getter values */
  function buildConfig(): ConfigWithCallbacks {
    const config = { ...staticConfig }
    for (const [key, getter] of syncGetters) {
      (config as any)[key] = getter()
    }
    return config
  }

  if (initialConfig) {
    store.configure(initialConfig, instanceId)
  }

  // Auto-reset when the component that registered this config unmounts.
  // Deferred to nextTick so the incoming page has a chance to take ownership
  // first — prevents a brief flash of default state during route transitions.
  onUnmounted(() => {
    nextTick(() => {
      store.reset(instanceId)
    })
  })

  /**
   * Patch individual fields without full reconfigure.
   * Only writes to the store if this instance is the current owner.
   */
  function patch(partial: ConfigWithCallbacks) {
    Object.assign(staticConfig, partial)
    if (store._ownerId === instanceId) {
      store.patch(partial)
    }
  }

  /**
   * Full reconfigure (replaces all fields).
   */
  function configure(config: ConfigWithCallbacks) {
    Object.assign(staticConfig, config)
    store.configure(config, instanceId)
  }

  /**
   * Watch a reactive source and auto-patch a field.
   * The getter is stored so reclaimWhen can evaluate it live.
   * Example: qa.sync(() => filterActive.value, 'filterActive')
   */
  function sync<K extends keyof ConfigWithCallbacks>(
    source: WatchSource,
    key: K,
  ) {
    // Store the getter for live evaluation on reclaim
    const getter = typeof source === 'function'
      ? source as () => any
      : () => (source as any).value
    syncGetters.set(key as string, getter)

    watch(source, (val) => {
      // Only write to store if this instance is the current owner.
      if (store._ownerId === instanceId) {
        store.patch({ [key]: val } as any)
      }
    })
  }

  /**
   * Re-apply the full config when `condition` becomes true.
   * Evaluates all sync getters live to get fresh values — no stale snapshots.
   *
   * Uses two mechanisms for reliability:
   * 1. Vue watch (pre-flush) — handles tab switches and reactive changes
   * 2. Router afterEach — handles route navigations (fires after route is
   *    committed but before Vue renders)
   */
  function reclaimWhen(condition: WatchSource<boolean>) {
    const condFn = typeof condition === 'function'
      ? condition as () => boolean
      : () => (condition as any).value

    // Pre-flush watch for reactive changes (tab switches, etc.)
    watch(condition, (active) => {
      if (active) {
        store.configure(buildConfig(), instanceId)
      }
    })

    // afterEach guard fires after route.path is updated but before Vue renders
    const router = useRouter()
    const removeGuard = router.afterEach(() => {
      if (condFn()) {
        store.configure(buildConfig(), instanceId)
      }
    })
    onUnmounted(() => { removeGuard() })
  }

  return {
    patch,
    configure,
    sync,
    reclaimWhen,
    store,
  }
}
